<template>
  <div class="fu-drform" data-test="fu-drawer-payment-form">
    <div class="fu-drform__from" data-test="fu-drawer-payment-from">
      <fu-icon name="text" :size="14" />
      <span v-if="fromNumber">Sends from <strong>{{ fromNumber }}</strong>, your practice’s texting number</span>
      <span v-else>Sends from your practice’s texting number</span>
    </div>

    <div>
      <label class="fu-drform__label" :for="`${uid}-tpl`">Template</label>
      <select
        v-if="templates.length"
        :id="`${uid}-tpl`"
        class="fu-drform__select"
        :value="templateId === null || templateId === undefined ? '' : String(templateId)"
        data-test="fu-drawer-template"
        @change="onTemplate($event.target.value)"
      >
        <option value="" disabled>Pick a payment template</option>
        <option v-for="t in templates" :key="t.value" :value="String(t.value)">{{ t.label }}</option>
      </select>
      <div v-else class="fu-drform__warn" data-test="fu-drawer-template-empty">
        <fu-icon name="alert" :size="15" />
        <span>You do not have any wiki templates tagged with "payment". Please create one first, tag it and try again</span>
      </div>
    </div>

    <div>
      <label class="fu-drform__label" :for="`${uid}-msg`">Message</label>
      <textarea
        :id="`${uid}-msg`"
        class="fu-drform__textarea"
        rows="5"
        :value="message"
        placeholder="Pick a template, then edit the text if you need to"
        data-test="fu-drawer-message"
        @input="$emit('message-input', $event.target.value)"
      ></textarea>
      <p class="fu-drform__hint">The secure payment link and amounts are added when the text sends. Saved to the commlog.</p>
      <div class="fu-drform__guidance"><slot name="guidance" /></div>
    </div>

    <div v-if="inOfficeAvailable || $slots['in-office']" class="fu-drform__in-office">
      <button
        v-if="inOfficeAvailable"
        type="button"
        class="fu-drform__in-office-btn"
        data-test="fu-drawer-in-office"
        @click="$emit('open-in-office')"
      ><fu-icon name="dollar" :size="15" />Take a card payment in the office instead</button>
      <slot name="in-office" />
    </div>
  </div>
</template>

<script>
/**
 * DrawerPaymentForm — the "Payment link" method = today's Collect Payment, the SAME function (Nilay
 * 2026-10-02): Moolah or Stripe link by text (CreateFollowup.vue:644–705, in the design's look).
 *
 *   fromNumber         the practice's texting number
 *   templates          [{ value, label }] — drawerMethods.templatesFor('payment', …)
 *   templateId, message  the payment text (renderSmsTemplate; the payment-link placeholder is LEFT for
 *                      the send — Moolah's backend fills it, the Stripe path fills it in
 *                      drawerPayload.stripePaymentText)
 *   inOfficeAvailable  show the in-office card button (a Stripe Connect site)
 * Slots:
 *   guidance   ⛔ the container's EXISTING <SmsGuidance> (today shows it on this form too)
 *   in-office  where the container may mount <StripePaymentModal> — the in-office card stays
 *              reachable from the same place as the link
 * Events: template-change(id), message-input(text), open-in-office. Presentational.
 */
import FuIcon from '../FuIcon.vue';

let seq = 0;

export default {
  name: 'DrawerPaymentForm',
  components: { FuIcon },
  props: {
    fromNumber: { type: String, default: '' },
    templates: { type: Array, default: () => [] },
    templateId: { type: [Number, String], default: null },
    message: { type: String, default: '' },
    inOfficeAvailable: { type: Boolean, default: false },
  },
  data() {
    seq += 1;
    return { uid: `fu-drpay-${seq}` };
  },
  methods: {
    onTemplate(v) {
      const t = this.templates.find(x => String(x.value) === v);
      this.$emit('template-change', t ? t.value : null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drform { display: flex; flex-direction: column; gap: 16px; }
.fu-drform__label { @include fu-dr-field-label; }
.fu-drform__select { @include fu-input; }
.fu-drform__textarea { @include fu-dr-textarea; }
.fu-drform__hint { @include fu-dr-hint; }
.fu-drform__guidance { margin-top: 8px; }

.fu-drform__from {
  @include fu-dr-soft-note;
  align-items: center;
  strong { font-weight: $fu-fw-bold; color: $fu-text; }
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drform__warn {
  @include fu-dr-warn-strip;
  .fu-icon { flex: none; margin-top: 1px; color: $fu-warning; }
}

.fu-drform__in-office {
  padding-top: 12px;
  border-top: 1px solid $fu-border;
}

.fu-drform__in-office-btn { @include fu-btn(secondary, sm); }

.fu-dr--phone .fu-drform__select,
.fu-dr--phone .fu-drform__textarea { @include fu-dr-phone-input; }
</style>
